import { useStoreList, useModusignDocument } from 'queries/useESignQueries.js';
import { Button, PrevButton, ButtonGroup } from '../ESignApplicationContainer.jsx';
import { useNavigate } from 'react-router-dom';
import SignLayout from './SignLayout.jsx';
import StoreInfo from './StoreInfo.jsx';
import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ESignApi from 'apis/eSginApis.ts';
import { useESign } from '../eSignApplicationContext.jsx';
import AgreementLayout from './AgreementLayout.jsx';
import { ROUTES } from 'App.js';
import useBusinessNumber from '../hooks/useBusinessNumber.jsx';
import useRedirect from '../hooks/useRedirect.jsx';
import useSignAction from '../hooks/useAction.jsx';
import Loading from 'components/Loading.jsx';
import { usePDF } from '../eSignPDFContext.tsx';

export default function SignStep() {
  const { businessNumber } = useBusinessNumber();
  const { redirectUrl, redirect } = useRedirect();
  const { status } = useSignAction();
  const navigate = useNavigate();

  const { data: storeList = [] } = useStoreList(businessNumber);
  const [storeId, setStoreId] = useState(null);
  const { data: modusignDocument, isModusignDocumentLoading } = useModusignDocument(
    storeId,
    status
  );
  const { checkedList } = useESign();
  const documentRef = useRef(null);

  // PDF 상태
  const { setStoreRefs, getStoreRefsForPDF, getIsReady } = usePDF();

  // 전송 상태
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (storeList.length < 1) return;

    setStoreRefs(storeList);

    const targetStore = storeList.find(
      (store) =>
        store.storeStatusType === 'REQUEST_OPEN_WRITING' ||
        store.storeStatusType === 'REQUEST_OPEN' ||
        store.storeStatusType === 'OPENED_COMPLETE' ||
        store.storeStatusType === 'OPERATIONAL'
    );

    if (targetStore) {
      setStoreId(targetStore?.storeId ?? null);
    }
  }, [storeList]);

  const getImgData = async (documentRef) => {
    const element = documentRef.current;
    const canvas = await html2canvas(element, { allowTaint: false, useCORS: true, scale: 1 }); // 다른 도메인 이미지 URL 허용
    return canvas.toDataURL('image/png');
  };

  const getMergedPdf = async () => {
    try {
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      // 1️⃣ 계약서 목록
      const storeRefs = getStoreRefsForPDF();

      for (let i = 0; i < storeRefs.length; i++) {
        const ref = storeRefs[i];

        if (!ref?.current) {
          throw Error('컴포넌트가 아직 렌더링되지 않았습니다');
        }

        const imgData = await getImgData(ref);
        const imgProps = pdf.getImageProperties(imgData);

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }

      // 2️⃣ 약관동의 페이지
      if (documentRef.current) {
        const imgData = await getImgData(documentRef);
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }

      // 3 전자계약서
      await new Promise((resolve, reject) => {
        const img = new Image();
        // img.crossOrigin = 'anonymous'; // CORS 허용
        img.src = modusignDocument.fileUrl;
        img.onload = () => {
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (img.height * pdfWidth) / img.width;

          pdf.addPage();
          pdf.addImage(img, 'PNG', 0, 0, pdfWidth, pdfHeight);
          resolve(); // 완료 시 resolve 호출
        };

        img.onerror = (err) => reject(err);
      });

      // 다운로드
      // pdf.save('contract.pdf');

      // 서버에 전송할 포맷
      const pdfBlob = pdf.output('blob');

      return pdfBlob;
    } catch (error) {
      alert('계약서 파일 생성 중 예기치 못한 에러가 발생했습니다. 잠시 후 다시 실행해 주세요.');
      console.error(`계약서 파일 생성 에러 : ${error}`);
      throw error;
    }
  };

  const submit = async () => {
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      if (!checkedList.slice(0, -1).every((v) => v)) {
        alert('이전 페이지에서 필수 항목 동의를 완료해 주세요');
        return;
      }

      if (!modusignDocument?.id) {
        alert('서명 절차를 완료해 주세요');
        return;
      }

      const pdf = await getMergedPdf();

      // 트랜젝션 예방을 위해 동기적으로 API 호출
      await ESignApi.postAgreementOfContract({
        businessRegistrationCertificateNumber: businessNumber,
        receiveMarketing: checkedList[checkedList.length - 1],
        receiveTerms: true,
      });

      await ESignApi.uploadContract({
        storeId: storeList.map(({ storeId }) => storeId),
        documentId: modusignDocument?.id,
        termsAccept: pdf,
      });

      alert('전자계약서 제출에 성공했습니다');
      redirect();
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const moveToPrev = () => {
    navigate(
      `${ROUTES.eSignAgreement}?businessNumber=${businessNumber}${redirectUrl ? `&redirectUrl=${redirectUrl}` : ''}`
    );
  };

  return (
    <>
      {/* PDF 추출 준비 완료 전, 제출 방지 */}
      {(!getIsReady(storeList) || isSubmitting || isModusignDocumentLoading) && <Loading />}
      <SignLayout storeId={storeId} />

      {/* PDF용 숨김 렌더링 */}
      <div style={{ position: 'fixed', left: -1000, top: 0, zIndex: 100000 }}>
        <StoreInfo isMobile={false} storeList={storeList} />
      </div>
      <div style={{ position: 'fixed', left: -2000, top: 0, zIndex: 100000 }}>
        <AgreementLayout isMobile={false} documentRef={documentRef} />
      </div>
      {/* ************** */}

      <ButtonGroup>
        <PrevButton onClick={moveToPrev}>이전 페이지</PrevButton>
        <Button
          onClick={submit}
          disabled={
            modusignDocument?.status != 'completed' ||
            isSubmitting ||
            isModusignDocumentLoading ||
            isSuccess ||
            !getIsReady(storeList)
          }
        >
          {isSuccess ? '제출 완료' : '제출하기'}
        </Button>
      </ButtonGroup>
    </>
  );
}
